import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Bell, User, LogOut, Settings, ChevronDown } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import styles from './Navbar.module.css';

interface NavbarProps {
  onMenuClick?: () => void;
  showMenuButton?: boolean;
}

const Navbar = ({ onMenuClick, showMenuButton = false }: NavbarProps) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className={styles.navbar}>
      <div className={styles.left}>
        {showMenuButton && (
          <button
            className={styles.menuButton}
            onClick={onMenuClick}
            aria-label="Apri menu"
          >
            <Menu size={24} />
          </button>
        )}

        <div className={styles.brand} onClick={() => navigate('/report')}>
          <span className={styles.logo}>SP</span>
          <span className={styles.brandName}>Saldo Perfetto</span>
        </div>
      </div>

      <div className={styles.right}>
        <button
          className={styles.iconButton}
          aria-label="Notifiche"
        >
          <Bell size={20} />
        </button>

        <div className={styles.profileWrapper} ref={profileRef}>
          <button
            className={styles.profileButton}
            onClick={() => setProfileOpen(!profileOpen)}
            aria-expanded={profileOpen}
            aria-haspopup="menu"
          >
            <div className={styles.avatar}>
              <User size={18} />
            </div>
            <span className={styles.userName}>{user?.username || 'Utente'}</span>
            <ChevronDown
              size={16}
              className={`${styles.chevron} ${profileOpen ? styles.open : ''}`}
            />
          </button>

          {profileOpen && (
            <div className={styles.dropdown} role="menu">
              <button
                className={styles.dropdownItem}
                onClick={() => {
                  setProfileOpen(false);
                  navigate('/impostazioni');
                }}
                role="menuitem"
              >
                <Settings size={18} />
                <span>Impostazioni</span>
              </button>
              <button
                className={styles.dropdownItem}
                onClick={handleLogout}
                role="menuitem"
              >
                <LogOut size={18} />
                <span>Esci</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
